import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import moment from 'moment';
import { merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import {
    IExpenseItem,
    ILocale,
    IPerDiemLocale,
    ITask
} from '../../../lookups';
import {
    ConfirmationService,
    type FileUpload
} from '../../../shared/efaact-primeng.module';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { StateService } from '@uirouter/core';
import { IUserInfo } from '../../../user-info';
import {
    LocaleEnum,
    PerDiemTypeEnum,
    ExpenseRequestTypeEnum,
    TravelTypeEnum
} from '../../expense-enums';
import {
    IExpenseDetailAttachment,
    IExpenseDetailReceipt,
    IExpenseReportDetailLine
} from '../../IExpenseReportDetail';
import { IExpenseLine } from '../../IExpenseLine';
import { IExpenseReceipt } from '../../IExpenseReceipt';
import { IExpenseSummary } from '../../IExpenseSummary';
import { IExpenseTask } from '../../IExpenseTask';
import { ITravelMode } from '../../ITravelMode';
import { ITravelRate } from '../../ITravelRate';
import { ITripLegType } from '../../ITripLegType';
import { UserPaymentMethod } from '../../UserPaymentMethod';

export interface ExpenseEntryTravelRatesRequest {
    kind: 'perdiem' | 'mileage';
    request: ITravelRate;
}

export interface ExpenseEntrySavePayload {
    line: IExpenseReportDetailLine;
}

type AddressField = 'perDiem' | 'from' | 'to';

@Component({
    selector: 'ef-expense-entry',
    templateUrl: './expense-entry.component.html',
    styleUrls: ['./expense-entry.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ConfirmationService],
    standalone: false
})
export class ExpenseEntryComponent implements OnInit, OnChanges, OnDestroy {
    @Input() travelTypeId: TravelTypeEnum = TravelTypeEnum.PerDiem;
    @Input() canEdit = true;
    @Input() reportDateEnabled = true;
    @Input() purposeRequired = false;
    /** Company setting ReceiptDescRequired — description only when ON. */
    @Input() receiptDescriptionRequired = false;
    @Input() saving = false;
    @Input() summary: IExpenseSummary;
    @Input() detail: IExpenseLine;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() tasks: ITask[] = [];
    @Input() expenseTasks: IExpenseTask[] = [];
    @Input() expenseItems: IExpenseItem[] = [];
    @Input() locales: ILocale[] = [];
    @Input() perDiemTypes: IPerDiemLocale[] = [];
    @Input() tripLegTypes: ITripLegType[] = [];
    @Input() travelModes: ITravelMode[] = [];
    @Input() travelRates: ITravelRate[] = [];
    @Input() selectedMileageRate: ITravelRate;
    @Input() userPaymentMethods: UserPaymentMethod[] = [];
    @Input() userInfo: IUserInfo;
    @Input() successMessage: string;
    @Input() failureMessage: string;

    @Output() save = new EventEmitter<ExpenseEntrySavePayload>();
    @Output() cancel = new EventEmitter<void>();
    @Output() travelRatesNeeded =
        new EventEmitter<ExpenseEntryTravelRatesRequest>();

    entryForm: UntypedFormGroup;
    attachments: IExpenseDetailAttachment[] = [];
    /** Attachment add/remove is outside the FormGroup — counts as a user change for Save. */
    private attachmentsChanged = false;
    /** Files currently being read, so a second add cannot duplicate them before onload. */
    private pendingAttachmentKeys = new Set<string>();
    showAddressDialog = false;
    addressField: AddressField = 'perDiem';
    addressContext = {
        city: '',
        state: '',
        zip: '',
        countryArea: '',
        destination: ''
    };
    private active = true;
    private seeded = false;
    /** Suppress From/To clear when Locale is set programmatically (seed / default). */
    private programmaticLocalePatch = false;

    constructor(
        private fb: UntypedFormBuilder,
        private log: LoggingService,
        private cd: ChangeDetectorRef,
        private confirm: ConfirmationService,
        private stateService: StateService
    ) {
        this.entryForm = this.fb.group({
            ReportDate: [null, Validators.required],
            Purpose: [null],
            SelectedTask: [null, Validators.required],
            SelectedExpenseItem: [null, Validators.required],
            TravelStartDate: [null],
            TravelEndDate: [null],
            Locale: [null],
            PerDiemTypeID: [null],
            TripLegType: [null],
            TravelRate: [null],
            TravelMode: [null],
            Mileage: [null],
            City1: [null],
            State1: [null],
            Zipcode1: [null],
            City2: [null],
            State2: [null],
            Zipcode2: [null],
            Country_Area: [null],
            Destination_Location: [null],
            ReceiptTax: [null],
            ReceiptTotal: [null],
            ReceiptDescription: [null],
            PaymentMethod: [null]
        });
    }

    ngOnInit(): void {
        this.applyTravelTypeValidators();
        this.entryForm.valueChanges
            .pipe(debounceTime(300), takeWhile(() => this.active))
            .subscribe(() => this.emitTravelRatesIfNeeded());

        // OnPush: refresh Save enablement when validity / dirty state changes
        merge(this.entryForm.statusChanges, this.entryForm.valueChanges)
            .pipe(debounceTime(0), takeWhile(() => this.active))
            .subscribe(() => this.cd.markForCheck());

        this.entryForm
            .get('Locale')
            ?.valueChanges.pipe(
                distinctUntilChanged(
                    (a, b) => a?.LocaleID === b?.LocaleID
                ),
                takeWhile(() => this.active)
            )
            .subscribe(locale => {
                if (this.isMileage) {
                    // Locale switch Domestic ↔ OCONUS clears From/To (same as #6028)
                    if (!this.programmaticLocalePatch && locale?.LocaleID) {
                        this.clearMileageLocations();
                    }
                } else {
                    this.entryForm.patchValue(
                        { PerDiemTypeID: null, TravelRate: null },
                        { emitEvent: false }
                    );
                }
                this.applyTravelTypeValidators();
                this.cd.markForCheck();
            });

        this.entryForm
            .get('PerDiemTypeID')
            ?.valueChanges.pipe(
                distinctUntilChanged(),
                takeWhile(() => this.active)
            )
            .subscribe(() => {
                this.applyTravelTypeValidators();
                this.cd.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['travelTypeId']) {
            this.applyTravelTypeValidators();
        }
        if (changes['purposeRequired']) {
            const ctrl = this.entryForm.get('Purpose');
            if (this.purposeRequired) {
                ctrl?.setValidators([Validators.required]);
            } else {
                ctrl?.clearValidators();
            }
            ctrl?.updateValueAndValidity({ emitEvent: false });
        }
        if (changes['receiptDescriptionRequired']) {
            this.applyTravelTypeValidators();
        }
        if (changes['reportDateEnabled'] || changes['canEdit']) {
            const reportDateCtrl = this.entryForm.get('ReportDate');
            if (!this.canEdit || !this.reportDateEnabled) {
                reportDateCtrl?.disable({ emitEvent: false });
            } else {
                reportDateCtrl?.enable({ emitEvent: false });
            }
        }
        if (
            !this.seeded &&
            (changes['summary'] || changes['detail'] || changes['travelTypeId'])
        ) {
            this.seedForm();
        }
        if (
            this.seeded &&
            (changes['expenseTasks'] ||
                changes['tasks'] ||
                changes['expenseItems'] ||
                changes['locales'] ||
                changes['perDiemTypes'] ||
                changes['tripLegTypes'] ||
                changes['travelModes'] ||
                changes['userPaymentMethods'])
        ) {
            this.seedLookupDependentFields();
            if (changes['perDiemTypes'] || changes['locales']) {
                // Receipt-required depends on PerDiemTypeDesc from the locale lookup
                this.applyTravelTypeValidators();
            }
        }
        if (changes['travelRates']) {
            this.applyTravelRateSelection();
        }
        this.cd.markForCheck();
    }

    ngOnDestroy(): void {
        this.active = false;
    }

    get title(): string {
        switch (this.travelTypeId) {
            case TravelTypeEnum.Mileage:
                return 'Mileage';
            case TravelTypeEnum.Receipt:
                return 'Receipt';
            default:
                return 'Per Diem';
        }
    }

    get isPerDiem(): boolean {
        return this.travelTypeId === TravelTypeEnum.PerDiem;
    }

    get isMileage(): boolean {
        return this.travelTypeId === TravelTypeEnum.Mileage;
    }

    get isReceipt(): boolean {
        return this.travelTypeId === TravelTypeEnum.Receipt;
    }

    /** Mileage supports Domestic and OCONUS only (Foreign excluded per #6028). */
    get mileageLocales(): ILocale[] {
        return (this.locales || []).filter(
            l =>
                l?.LocaleID === LocaleEnum.Domestic ||
                l?.LocaleID === LocaleEnum.OCONUS
        );
    }

    get isDomesticMileageLocale(): boolean {
        return this.addressLocaleId === LocaleEnum.Domestic;
    }

    /** Bound TravelRate (after rates load) or store-selected rate for display/save. */
    get displayMileageRate(): ITravelRate {
        return (
            (this.entryForm?.value?.TravelRate as ITravelRate) ||
            this.selectedMileageRate
        );
    }

    /** Matches Detail grid "Exp Line" (`ExpReportNbr-SequenceId`). */
    get reportLineLabel(): string {
        const nbr = this.summary?.ExpReportNbr;
        if (nbr == null || nbr <= 0) {
            return '';
        }
        const seq = this.detail?.SequenceId;
        if (seq != null && seq > 0) {
            return `${nbr}-${seq}`;
        }
        // New / unsaved line — SequenceId assigned on insert
        return `${nbr}-`;
    }

    get showReceiptSection(): boolean {
        // Available on every expense entry type (optional except where receiptRequired).
        return true;
    }

    /**
     * Receipt tax/total/payment required for standalone Receipt, or for per diem
     * types whose PerDiemTypeDesc includes "(receipt required)" from the API.
     * Description is required only when that applies and ReceiptDescRequired is ON.
     */
    get receiptRequired(): boolean {
        if (this.isReceipt) {
            return true;
        }
        if (!this.isPerDiem) {
            return false;
        }
        const typeId = this.entryForm?.value?.PerDiemTypeID;
        if (typeId == null || typeId === '') {
            return false;
        }
        const desc = this.selectedPerDiemTypeDesc;
        return /receipt\s*required/i.test(desc);
    }

    /** Description label/validator: receipt required AND company setting ON. */
    get receiptDescriptionFieldRequired(): boolean {
        return this.receiptRequired && this.receiptDescriptionRequired;
    }

    /** Resolve PerDiemTypeDesc for the selected type (locale-aware; numeric-safe). */
    private get selectedPerDiemTypeDesc(): string {
        const typeId = this.entryForm?.value?.PerDiemTypeID;
        if (typeId == null || typeId === '') {
            return '';
        }
        const typeNum = Number(typeId);
        const localeId = this.entryForm?.value?.Locale?.LocaleID;
        const types = this.perDiemTypes || [];
        const byLocale = types.find(
            t =>
                Number(t.PerDiemTypeID) === typeNum &&
                (localeId == null || Number(t.LocaleID) === Number(localeId))
        );
        if (byLocale?.PerDiemTypeDesc) {
            return byLocale.PerDiemTypeDesc;
        }
        return (
            types.find(t => Number(t.PerDiemTypeID) === typeNum)
                ?.PerDiemTypeDesc || ''
        );
    }

    /** Show Tax/Total/Payment errors once receipt is required and field is empty. */
    receiptFieldError(controlName: string): boolean {
        if (!this.receiptRequired) {
            return false;
        }
        if (
            controlName === 'ReceiptDescription' &&
            !this.receiptDescriptionFieldRequired
        ) {
            return false;
        }
        const ctrl = this.entryForm?.get(controlName);
        if (!ctrl) {
            return false;
        }
        const v = ctrl.value;
        if (controlName === 'PaymentMethod') {
            return !v;
        }
        if (controlName === 'ReceiptDescription') {
            return v == null || String(v).trim() === '';
        }
        // Tax / Total — treat null/'' as missing; allow 0
        return v == null || v === '';
    }

    get showMealsRate(): boolean {
        const typeId = this.entryForm?.value?.PerDiemTypeID;
        return (
            typeId === PerDiemTypeEnum.MIE ||
            typeId === PerDiemTypeEnum.BothLodgingandMIE ||
            typeId === PerDiemTypeEnum.LocalMealRate ||
            typeId === PerDiemTypeEnum.ProportionalMealRate ||
            typeId === PerDiemTypeEnum.LocalIncidental
        );
    }

    get showLodgingRate(): boolean {
        const typeId = this.entryForm?.value?.PerDiemTypeID;
        return (
            typeId === PerDiemTypeEnum.Lodging ||
            typeId === PerDiemTypeEnum.BothLodgingandMIE
        );
    }

    /**
     * Initial open tabs only (General + next two). Updated by accordion
     * two-way binding when the user expands/collapses — never reset on data entry.
     */
    activeAccordionIndexes: number[] = [0, 1, 2];

    /**
     * Save only when the form is valid, required receipt fields are present,
     * the user changed something, and calculated amount is not $0.
     * Rate auto-bind must not count as a change (see applyTravelRateSelection).
     */
    get saveDisabled(): boolean {
        if (!this.canEdit || this.saving) {
            return true;
        }
        if (this.entryForm.invalid || this.hasMissingRequiredReceiptFields()) {
            return true;
        }
        // Per diem / mileage: block save when rates returned Amount = 0
        if (!this.isReceipt) {
            const rate = (this.entryForm?.value?.TravelRate ||
                this.selectedMileageRate) as ITravelRate;
            if (rate != null && rate.Amount != null && Number(rate.Amount) === 0) {
                return true;
            }
        }
        const unchanged =
            this.entryForm.pristine && !this.attachmentsChanged;
        return unchanged;
    }

    /**
     * Explicit check so Save stays blocked even if Angular validators were not
     * re-applied yet after per-diem type / lookup changes.
     */
    private hasMissingRequiredReceiptFields(): boolean {
        if (!this.receiptRequired) {
            return false;
        }
        const v = this.entryForm.getRawValue();
        if (v.ReceiptTax == null || v.ReceiptTax === '') {
            return true;
        }
        if (v.ReceiptTotal == null || v.ReceiptTotal === '') {
            return true;
        }
        if (!v.PaymentMethod) {
            return true;
        }
        if (this.receiptDescriptionFieldRequired) {
            const desc = v.ReceiptDescription;
            if (desc == null || String(desc).trim() === '') {
                return true;
            }
        }
        return false;
    }

    get hasUnsavedChanges(): boolean {
        return !this.entryForm.pristine || this.attachmentsChanged;
    }

    get taskOptions(): IExpenseTask[] {
        if (this.expenseTasks?.length) {
            const seen = new Set<string>();
            // Match mobile: ef_expenseitemtask_get returns time-only auths
            // with IsActive_Task=0 (AuthTypeID not in 2=Expense, 3=TimeExp).
            return this.expenseTasks.filter(t => {
                if (t.IsActive_Task !== true) {
                    return false;
                }
                const key = `${t.EmpAuthID}|${t.TaskLongName}`;
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
        }
        return (this.tasks || []).map(
            t =>
                ({
                    EmpAuthID: String((t as any).EmpAuthID || t.TaskListID),
                    TaskLongName: t.TaskLongName,
                    TaskListID: t.TaskListID,
                    EIListID: null,
                    ExpenseItem: null
                }) as IExpenseTask
        );
    }

    get expenseItemOptions(): Array<IExpenseItem | IExpenseTask> {
        const task = this.entryForm?.value?.SelectedTask as IExpenseTask;
        const currentId = this.currentExpenseItemListId;
        let items: Array<IExpenseItem | IExpenseTask> = [];
        if (task && this.expenseTasks?.length) {
            const matched = this.expenseTasks.filter(
                x =>
                    String(x.EmpAuthID) === String(task.EmpAuthID) &&
                    x.TaskLongName === task.TaskLongName
            );
            if (matched.length) {
                items = matched;
            }
        }
        if (!items.length) {
            items = this.expenseItems || [];
        }
        // Match mobile: hide inactive items, but keep the saved item so
        // editing an older expense still shows its original selection (#6133).
        return items.filter(item =>
            this.isExpenseItemOptionVisible(item, currentId)
        );
    }

    private get currentExpenseItemListId(): string | null {
        const selected = this.entryForm?.value?.SelectedExpenseItem as
            | IExpenseItem
            | IExpenseTask
            | null;
        if (selected?.EIListID != null && String(selected.EIListID) !== '') {
            return String(selected.EIListID);
        }
        const fromDetail = this.detail?.EIListID;
        if (fromDetail != null && String(fromDetail) !== '') {
            return String(fromDetail);
        }
        return null;
    }

    private isExpenseItemOptionVisible(
        item: IExpenseItem | IExpenseTask,
        currentId: string | null
    ): boolean {
        const id = String(
            (item as IExpenseItem).EIListID || (item as IExpenseTask).EIListID
        );
        if (currentId && id === currentId) {
            return true;
        }
        const taskItem = item as IExpenseTask;
        if (typeof taskItem.IsActive_ExpenseITem === 'boolean') {
            return taskItem.IsActive_ExpenseITem === true;
        }
        const lookupItem = item as IExpenseItem;
        if (typeof lookupItem.IsActive === 'boolean') {
            return lookupItem.IsActive === true;
        }
        return true;
    }

    get filteredPerDiemTypes(): IPerDiemLocale[] {
        const localeId = this.entryForm?.value?.Locale?.LocaleID;
        if (localeId == null) {
            return this.perDiemTypes || [];
        }
        return (this.perDiemTypes || []).filter(
            x => Number(x.LocaleID) === Number(localeId)
        );
    }

    get locationDisplay(): string {
        const v = this.entryForm.value;
        if (v.Destination_Location || v.Country_Area) {
            return [v.Destination_Location, v.Country_Area]
                .filter(Boolean)
                .join(', ');
        }
        return [v.City1, v.State1, v.Zipcode1].filter(Boolean).join(', ');
    }

    get fromLocationDisplay(): string {
        const v = this.entryForm.value;
        if (!this.isDomesticMileageLocale) {
            return [v.City1, v.State1].filter(Boolean).join(', ');
        }
        return [v.City1, v.State1, v.Zipcode1].filter(Boolean).join(', ');
    }

    get toLocationDisplay(): string {
        const v = this.entryForm.value;
        if (!this.isDomesticMileageLocale) {
            return [v.City2, v.State2].filter(Boolean).join(', ');
        }
        return [v.City2, v.State2, v.Zipcode2].filter(Boolean).join(', ');
    }

    get addressLocaleId(): number {
        return (
            this.entryForm.value.Locale?.LocaleID || LocaleEnum.Domestic
        );
    }

    get addressDialogHeader(): string {
        if (this.addressField === 'from' || this.addressField === 'to') {
            return this.isDomesticMileageLocale
                ? 'Enter City/State/Zip'
                : 'Enter Area/Location';
        }
        return 'Travel Location';
    }

    onLocationClick(): void {
        this.addressField = 'perDiem';
        const v = this.entryForm.value;
        this.addressContext = {
            city: v.City1 || '',
            state: v.State1 || '',
            zip: v.Zipcode1 || '',
            countryArea: v.Country_Area || '',
            destination: v.Destination_Location || ''
        };
        this.showAddressDialog = true;
    }

    onFromLocationClick(): void {
        this.addressField = 'from';
        const v = this.entryForm.value;
        if (!this.isDomesticMileageLocale) {
            // OCONUS: City1 = location, State1 = area (#6028)
            this.addressContext = {
                city: '',
                state: '',
                zip: '',
                countryArea: v.State1 || '',
                destination: v.City1 || ''
            };
        } else {
            this.addressContext = {
                city: v.City1 || '',
                state: v.State1 || '',
                zip: v.Zipcode1 || '',
                countryArea: '',
                destination: ''
            };
        }
        this.showAddressDialog = true;
    }

    onToLocationClick(): void {
        this.addressField = 'to';
        const v = this.entryForm.value;
        if (!this.isDomesticMileageLocale) {
            this.addressContext = {
                city: '',
                state: '',
                zip: '',
                countryArea: v.State2 || '',
                destination: v.City2 || ''
            };
        } else {
            this.addressContext = {
                city: v.City2 || '',
                state: v.State2 || '',
                zip: v.Zipcode2 || '',
                countryArea: '',
                destination: ''
            };
        }
        this.showAddressDialog = true;
    }

    onSetAddressData(data: any): void {
        this.log.debug({
            type: 'ExpenseEntryComponent:onSetAddressData',
            data,
            field: this.addressField
        });
        if (!data) {
            this.showAddressDialog = false;
            return;
        }
        if (this.addressField === 'from' || this.addressField === 'to') {
            this.applyMileageAddressData(data);
        } else {
            this.entryForm.patchValue({
                City1: data?.City?.City || data?.City || null,
                State1: data?.State?.State || data?.State || null,
                Zipcode1: data?.PostalCode || data?.ZipCode || null,
                Country_Area:
                    data?.CountryArea?.CountryArea ||
                    data?.Country_Area ||
                    null,
                Destination_Location:
                    data?.Location?.Destination ||
                    data?.Destination ||
                    null
            });
        }
        this.showAddressDialog = false;
        // Dialog selection is user-driven; programmatic patchValue does not mark dirty
        this.entryForm.markAsDirty();
        this.emitTravelRatesIfNeeded();
        this.cd.markForCheck();
    }

    private applyMileageAddressData(data: any): void {
        const isFrom = this.addressField === 'from';
        if (!this.isDomesticMileageLocale) {
            const area =
                data?.CountryArea?.CountryArea || data?.Country_Area || null;
            const location =
                data?.Location?.Destination || data?.Destination || null;
            if (isFrom) {
                this.entryForm.patchValue({
                    Zipcode1: null,
                    City1: location,
                    State1: area
                });
            } else {
                this.entryForm.patchValue({
                    Zipcode2: null,
                    City2: location,
                    State2: area
                });
            }
            return;
        }
        if (isFrom) {
            this.entryForm.patchValue({
                City1: data?.City?.City || data?.City || null,
                State1: data?.State?.State || data?.State || null,
                Zipcode1: data?.PostalCode || data?.ZipCode || null
            });
        } else {
            this.entryForm.patchValue({
                City2: data?.City?.City || data?.City || null,
                State2: data?.State?.State || data?.State || null,
                Zipcode2: data?.PostalCode || data?.ZipCode || null
            });
        }
    }

    onAttachmentUpload(
        event: { files: File[] },
        uploader?: FileUpload
    ): void {
        const files = event?.files || [];
        // PrimeNG FileUpload re-emits its full internal queue on each auto-upload.
        // Skip files already listed (or still being read) so 2nd+ adds do not duplicate.
        const alreadyListed = new Set<string>([
            ...this.attachments.map(a =>
                this.attachmentListKey(a.Filename, a.Filesize)
            ),
            ...this.pendingAttachmentKeys
        ]);
        const newFiles: File[] = [];
        for (const file of files) {
            const key = this.attachmentListKey(file.name, file.size);
            if (alreadyListed.has(key)) {
                continue;
            }
            alreadyListed.add(key);
            this.pendingAttachmentKeys.add(key);
            newFiles.push(file);
        }
        uploader?.clear();
        newFiles.forEach(file => {
            const key = this.attachmentListKey(file.name, file.size);
            const reader = new FileReader();
            reader.onload = () => {
                this.pendingAttachmentKeys.delete(key);
                const result = String(reader.result || '');
                const base64 = result.includes(',')
                    ? result.split(',')[1]
                    : result;
                this.attachments = [
                    ...this.attachments,
                    {
                        File: base64,
                        Filename: file.name,
                        Filesize: file.size,
                        Filemimetype: file.type,
                        IsPDF: file.type === 'application/pdf',
                        IsImage: (file.type || '').startsWith('image/'),
                        deleteable: true
                    }
                ];
                this.attachmentsChanged = true;
                this.cd.markForCheck();
            };
            reader.onerror = () => {
                this.pendingAttachmentKeys.delete(key);
            };
            reader.readAsDataURL(file);
        });
    }

    private attachmentListKey(name: string, size: number): string {
        return `${name || ''}\0${size || 0}`;
    }

    onRemoveAttachment(index: number): void {
        this.attachments = this.attachments.filter((_, i) => i !== index);
        this.attachmentsChanged = true;
        this.cd.markForCheck();
    }

    /**
     * Same route as receipt-list "View" links: `/receipt/:receiptId`
     * where receiptId is the image-DB attachment Unique_id.
     */
    getAttachmentViewHref(file: IExpenseDetailAttachment): string | null {
        const receiptId = file?.Unique_Id || file?.File_Unique_Id;
        if (!receiptId) {
            return null;
        }
        return this.stateService.href('receipt', { receiptId });
    }

    onAttachmentNameClick(
        file: IExpenseDetailAttachment,
        event: Event
    ): void {
        const href = this.getAttachmentViewHref(file);
        if (href) {
            // Saved attachment — let the anchor open /receipt/:id like list View
            return;
        }
        event.preventDefault();
        this.openUnsavedAttachment(file);
    }

    private openUnsavedAttachment(file: IExpenseDetailAttachment): void {
        if (!file?.File) {
            return;
        }
        const mime = file.Filemimetype || 'application/octet-stream';
        const raw = String(file.File);
        const dataUrl = raw.startsWith('data:')
            ? raw
            : `data:${mime};base64,${raw}`;
        window.open(dataUrl, '_blank');
    }

    onSave(): void {
        this.applyTravelTypeValidators();
        if (this.saveDisabled || this.hasMissingRequiredReceiptFields()) {
            this.entryForm.markAllAsTouched();
            this.cd.markForCheck();
            return;
        }
        const line = this.buildLinePayload();
        this.log.debug({ type: 'ExpenseEntryComponent:onSave', line });
        this.save.emit({ line });
    }

    onCancel(): void {
        if (!this.canEdit || !this.hasUnsavedChanges) {
            this.cancel.emit();
            return;
        }
        this.confirm.confirm({
            message: 'Discard unsaved changes?',
            header: 'Cancel',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.cancel.emit()
        });
    }

    private seedForm(): void {
        const reportDate = this.summary?.ReportDate
            ? moment(this.summary.ReportDate).toDate()
            : new Date();
        const detail = this.detail || {};
        const patch: Record<string, unknown> = {
            ReportDate: reportDate,
            Purpose: detail.Purpose || null,
            TravelStartDate: detail.TravelStartDate
                ? moment(detail.TravelStartDate).toDate()
                : reportDate,
            TravelEndDate: detail.TravelEndDate
                ? moment(detail.TravelEndDate).toDate()
                : reportDate,
            PerDiemTypeID: detail.PerDiemTypeID || null,
            Mileage: detail.Mileage ?? null,
            City1: detail.City1 || null,
            State1: detail.State1 || null,
            Zipcode1: detail.Zipcode1 || null,
            City2: detail.City2 || null,
            State2: detail.State2 || null,
            Zipcode2: detail.Zipcode2 || null,
            Country_Area: detail.Country_Area || null,
            Destination_Location: detail.Destination_Location || null,
            ReceiptTax:
                this.seedReceipt()?.ReceiptTax ??
                detail.Line_ReceiptTax ??
                null,
            ReceiptTotal:
                this.seedReceipt()?.ReceiptTotal ??
                detail.Line_ReceiptTotal ??
                null,
            ReceiptDescription:
                this.seedReceipt()?.ReceiptDescription || null
        };

        if (detail.LocaleID && this.locales?.length) {
            patch['Locale'] =
                this.locales.find(l => l.LocaleID === detail.LocaleID) || null;
        }
        if (detail.TripLegTypeID && this.tripLegTypes?.length) {
            patch['TripLegType'] =
                this.tripLegTypes.find(
                    t => t.TripLegTypeID === detail.TripLegTypeID
                ) || null;
        }
        if (detail.RateTypeID && this.travelModes?.length) {
            patch['TravelMode'] =
                this.travelModes.find(m => m.RateTypeID === detail.RateTypeID) ||
                null;
        }

        this.attachments = this.mapSeedAttachments();
        this.attachmentsChanged = false;

        this.entryForm.patchValue(patch, { emitEvent: false });
        this.seedLookupDependentFields();
        const reportDateCtrl = this.entryForm.get('ReportDate');
        if (!this.canEdit || !this.reportDateEnabled) {
            reportDateCtrl?.disable({ emitEvent: false });
        } else {
            reportDateCtrl?.enable({ emitEvent: false });
        }
        this.seeded = true;
        this.attachmentsChanged = false;
        // Re-apply after seed so locale/type-dependent rules (destination, lodging receipt) match data
        this.applyTravelTypeValidators();
        this.entryForm.markAsPristine();
        this.entryForm.markAsUntouched();
        // seedForm patches with emitEvent:false, so request rates explicitly on open
        this.emitTravelRatesIfNeeded();
        this.cd.markForCheck();
    }

    /** Prefer nested mobile receipt over line aggregates when seeding. */
    private seedReceipt(): IExpenseDetailReceipt | IExpenseReceipt | null {
        const detail = this.detail;
        if (!detail) {
            return null;
        }
        if (detail.expensereceipt) {
            return detail.expensereceipt;
        }
        return detail.Receipts?.length ? detail.Receipts[0] : null;
    }

    private mapSeedAttachments(): IExpenseDetailAttachment[] {
        const receipt = this.detail?.expensereceipt;
        const raw = receipt?.attachment || [];
        if (!raw.length) {
            return [];
        }
        return raw.map(a => {
            const mime = a.Filemimetype || '';
            return {
                Unique_Id: a.Unique_Id,
                File_Unique_Id: a.File_Unique_Id,
                File: a.File,
                Filename: a.Filename || 'Attachment',
                Filesize: a.Filesize,
                Filemimetype: mime,
                IsPDF:
                    a.IsPDF ??
                    String(mime).toLowerCase().includes('pdf'),
                IsImage:
                    a.IsImage ??
                    String(mime).toLowerCase().startsWith('image'),
                deleteable: a.deleteable !== false
            };
        });
    }

    private seedLookupDependentFields(): void {
        const detail = this.detail || {};
        const patch: Record<string, unknown> = {};
        const wasPristine = this.entryForm.pristine;

        if (detail.LocaleID && this.locales?.length && !this.entryForm.value.Locale) {
            patch['Locale'] =
                this.locales.find(l => l.LocaleID === detail.LocaleID) || null;
        }
        if (
            detail.TripLegTypeID &&
            this.tripLegTypes?.length &&
            !this.entryForm.value.TripLegType
        ) {
            patch['TripLegType'] =
                this.tripLegTypes.find(
                    t => t.TripLegTypeID === detail.TripLegTypeID
                ) || null;
        }
        if (
            detail.RateTypeID &&
            this.travelModes?.length &&
            !this.entryForm.value.TravelMode
        ) {
            patch['TravelMode'] =
                this.travelModes.find(m => m.RateTypeID === detail.RateTypeID) ||
                null;
        }

        const taskOptions = this.taskOptions;
        if (
            (detail.RptTask || detail.EmpAuthID) &&
            taskOptions?.length &&
            !this.entryForm.value.SelectedTask
        ) {
            patch['SelectedTask'] =
                taskOptions.find(
                    t =>
                        (detail.EmpAuthID != null &&
                            String(t.EmpAuthID) === String(detail.EmpAuthID)) ||
                        (detail.RptTask && t.TaskLongName === detail.RptTask)
                ) || null;
        }

        // Patch task first so expenseItemOptions can filter by selected task.
        if (Object.keys(patch).length) {
            this.programmaticLocalePatch = !!patch['Locale'];
            this.entryForm.patchValue(patch, { emitEvent: false });
            this.programmaticLocalePatch = false;
        }

        // Mileage defaults Locale to Domestic when lookups arrive (#6028)
        this.ensureDefaultMileageLocale();

        if (detail.EIListID && !this.entryForm.value.SelectedExpenseItem) {
            const expenseItemOptions = this.expenseItemOptions;
            const item =
                expenseItemOptions?.find(
                    (x: IExpenseItem | IExpenseTask) =>
                        String(
                            (x as IExpenseItem).EIListID ||
                                (x as IExpenseTask).EIListID
                        ) === String(detail.EIListID)
                ) || null;
            if (item) {
                this.entryForm.patchValue(
                    { SelectedExpenseItem: item },
                    { emitEvent: false }
                );
            }
        }

        // Payment methods often load after first seed
        if (
            !this.entryForm.value.PaymentMethod &&
            this.userPaymentMethods?.length
        ) {
            const receipt = this.seedReceipt();
            const creditId =
                (receipt as IExpenseDetailReceipt)?.creditaccountlistid ||
                (receipt as IExpenseReceipt)?.Creditaccountlistid;
            const paymentName =
                (receipt as IExpenseDetailReceipt)?.PaymentMethod ||
                (receipt as IExpenseReceipt)?.PaymentMethod;
            const match =
                this.userPaymentMethods.find(
                    m =>
                        (creditId &&
                            String(m.Creditaccountlistid) ===
                                String(creditId)) ||
                        (paymentName &&
                            String(m.Paymentmethod).toLowerCase() ===
                                String(paymentName).toLowerCase())
                ) || null;
            if (match) {
                this.entryForm.patchValue(
                    { PaymentMethod: match },
                    { emitEvent: false }
                );
            }
        }

        // Attachments from mobile GET may arrive only after detail is fully mapped
        if (
            !this.attachmentsChanged &&
            !this.attachments.length &&
            this.detail?.expensereceipt?.attachment?.length
        ) {
            this.attachments = this.mapSeedAttachments();
        }

        // Locale / trip leg / travel mode often arrive after first seed — load rates then
        if (
            patch['Locale'] ||
            patch['TripLegType'] ||
            patch['TravelMode']
        ) {
            this.emitTravelRatesIfNeeded();
        }
        if (wasPristine) {
            this.entryForm.markAsPristine();
        }
        this.cd.markForCheck();
    }

    /**
     * When travel rates arrive, bind the Destination that matches the seeded line
     * (not only when a single rate is returned).
     * Does not treat rate auto-bind as a user edit (preserves pristine for Save).
     */
    private applyTravelRateSelection(): void {
        if (!this.travelRates?.length) {
            return;
        }
        if (this.isMileage) {
            this.applyMileageRateSelection();
            return;
        }
        const current = this.entryForm.get('TravelRate')?.value as ITravelRate;
        const dest =
            current?.Destination ||
            this.entryForm.get('Destination_Location')?.value ||
            this.detail?.Destination_Location;
        let match: ITravelRate =
            dest != null && dest !== ''
                ? this.travelRates.find(r => r.Destination === dest)
                : null;
        if (!match && this.travelRates.length === 1) {
            match = this.travelRates[0];
        }
        if (!match) {
            return;
        }
        if (
            current?.Destination === match.Destination &&
            current?.Amount === match.Amount &&
            current?.Meals_Rate === match.Meals_Rate
        ) {
            return;
        }
        const wasPristine = this.entryForm.pristine;
        this.entryForm.patchValue({ TravelRate: match }, { emitEvent: false });
        if (match.Destination) {
            this.entryForm.patchValue(
                { Destination_Location: match.Destination },
                { emitEvent: false }
            );
        }
        this.entryForm.updateValueAndValidity({ emitEvent: false });
        if (wasPristine) {
            this.entryForm.markAsPristine();
        }
        this.cd.markForCheck();
    }

    /** Mirror prior mileage popup: pick rate by From state (domestic) or first rate (OCONUS). */
    private applyMileageRateSelection(): void {
        const localeId =
            this.entryForm.get('Locale')?.value?.LocaleID ??
            LocaleEnum.Domestic;
        const state1 = this.locationText(
            this.entryForm.get('State1')?.value
        );
        let match: ITravelRate = null;
        if (localeId !== LocaleEnum.Domestic) {
            match = this.travelRates[0];
        } else if (state1) {
            if (!this.travelRates.some(r => r.State === state1)) {
                return;
            }
            match = this.travelRates.find(r => r.State === state1) || null;
        } else {
            match = this.travelRates[0];
        }
        if (!match) {
            return;
        }
        const current = this.entryForm.get('TravelRate')?.value as ITravelRate;
        if (
            current?.Amount === match.Amount &&
            current?.Mileage_Rate === match.Mileage_Rate &&
            current?.Destination === match.Destination
        ) {
            return;
        }
        const wasPristine = this.entryForm.pristine;
        this.entryForm.patchValue({ TravelRate: match }, { emitEvent: false });
        if (wasPristine) {
            this.entryForm.markAsPristine();
        }
        this.cd.markForCheck();
    }

    /**
     * Align required rules by travel type (and keep Save disabled until valid).
     * Per diem: dates + order, locale/type/trip leg, destination by locale, lodging receipt.
     * Mileage: dates, mode, miles, From/To location (zip or state).
     * Receipt: tax, total, description, payment.
     */
    private applyTravelTypeValidators(): void {
        const start = this.entryForm.get('TravelStartDate');
        const end = this.entryForm.get('TravelEndDate');
        const locale = this.entryForm.get('Locale');
        const perDiemType = this.entryForm.get('PerDiemTypeID');
        const tripLeg = this.entryForm.get('TripLegType');
        const travelRate = this.entryForm.get('TravelRate');
        const countryArea = this.entryForm.get('Country_Area');
        const destination = this.entryForm.get('Destination_Location');
        const mileage = this.entryForm.get('Mileage');
        const mode = this.entryForm.get('TravelMode');
        const receiptTotal = this.entryForm.get('ReceiptTotal');
        const receiptTax = this.entryForm.get('ReceiptTax');
        const receiptDesc = this.entryForm.get('ReceiptDescription');
        const payment = this.entryForm.get('PaymentMethod');

        [
            start,
            end,
            locale,
            perDiemType,
            tripLeg,
            travelRate,
            countryArea,
            destination,
            mileage,
            mode,
            receiptTotal,
            receiptTax,
            receiptDesc,
            payment
        ].forEach(c => c?.clearValidators());
        this.entryForm.clearValidators();

        if (this.isPerDiem) {
            start?.setValidators([Validators.required]);
            end?.setValidators([Validators.required]);
            locale?.setValidators([Validators.required]);
            perDiemType?.setValidators([Validators.required]);
            tripLeg?.setValidators([Validators.required]);
            this.entryForm.setValidators([this.validateTravelDateOrder]);
            this.applyPerDiemLocationValidators();
            if (this.receiptRequired) {
                receiptTotal?.setValidators([Validators.required]);
                receiptTax?.setValidators([Validators.required]);
                payment?.setValidators([Validators.required]);
                if (this.receiptDescriptionFieldRequired) {
                    receiptDesc?.setValidators([Validators.required]);
                }
            }
        } else if (this.isMileage) {
            start?.setValidators([Validators.required]);
            end?.setValidators([Validators.required]);
            locale?.setValidators([Validators.required]);
            mileage?.setValidators([Validators.required]);
            mode?.setValidators([Validators.required]);
            this.entryForm.setValidators([
                this.validateTravelDateOrder,
                this.validateMileageLocations
            ]);
        } else if (this.isReceipt) {
            receiptTotal?.setValidators([Validators.required]);
            receiptTax?.setValidators([Validators.required]);
            payment?.setValidators([Validators.required]);
            if (this.receiptDescriptionFieldRequired) {
                receiptDesc?.setValidators([Validators.required]);
            }
        }

        [
            start,
            end,
            locale,
            perDiemType,
            tripLeg,
            travelRate,
            countryArea,
            destination,
            mileage,
            mode,
            receiptTotal,
            receiptTax,
            receiptDesc,
            payment
        ].forEach(c => c?.updateValueAndValidity({ emitEvent: false }));
        this.entryForm.updateValueAndValidity({ emitEvent: false });

        if (this.receiptRequired) {
            this.openReceiptAccordion();
            this.markEmptyRequiredReceiptFields();
        }
    }

    /** Accordion index of the Receipt fields tab (depends on visible travel tabs). */
    private get receiptAccordionIndex(): number {
        if (this.isPerDiem) {
            return 3; // General, Travel, Travel Location, Receipt
        }
        if (this.isMileage) {
            return 4; // General, Travel, From, To, Receipt
        }
        return 1; // General, Receipt
    }

    /** Expand Receipt when tax/total/payment become required. */
    private openReceiptAccordion(): void {
        const idx = this.receiptAccordionIndex;
        const open = Array.isArray(this.activeAccordionIndexes)
            ? this.activeAccordionIndexes
            : [];
        if (!open.includes(idx)) {
            this.activeAccordionIndexes = [...open, idx];
        }
    }

    /** Show field errors immediately for empty required receipt controls. */
    private markEmptyRequiredReceiptFields(): void {
        const markIfEmpty = (
            name: string,
            isEmpty: (value: unknown) => boolean
        ): void => {
            const ctrl = this.entryForm.get(name);
            if (ctrl && isEmpty(ctrl.value)) {
                // PrimeNG float labels style .ng-invalid.ng-dirty (not touched alone)
                ctrl.markAsDirty({ onlySelf: true });
                ctrl.markAsTouched({ onlySelf: true });
                ctrl.updateValueAndValidity({ emitEvent: false });
            }
        };

        markIfEmpty('ReceiptTax', v => v == null || v === '');
        markIfEmpty('ReceiptTotal', v => v == null || v === '');
        markIfEmpty('PaymentMethod', v => !v);
        if (this.receiptDescriptionFieldRequired) {
            markIfEmpty(
                'ReceiptDescription',
                v => v == null || String(v).trim() === ''
            );
        }
        this.cd.markForCheck();
    }

    private applyPerDiemLocationValidators(): void {
        const localeId = this.entryForm.get('Locale')?.value?.LocaleID;
        const travelRate = this.entryForm.get('TravelRate');
        const countryArea = this.entryForm.get('Country_Area');
        const destination = this.entryForm.get('Destination_Location');

        travelRate?.clearValidators();
        countryArea?.clearValidators();
        destination?.clearValidators();

        if (
            localeId === LocaleEnum.Foreign ||
            localeId === LocaleEnum.OCONUS
        ) {
            countryArea?.setValidators([Validators.required]);
            destination?.setValidators([Validators.required]);
        } else {
            // Domestic (and default): destination / travel rate required (prior popup)
            travelRate?.setValidators([Validators.required]);
        }
    }

    private validateTravelDateOrder: ValidatorFn = (
        group: AbstractControl
    ): ValidationErrors | null => {
        const start = group.get('TravelStartDate')?.value;
        const end = group.get('TravelEndDate')?.value;
        if (start && end && moment(start).isAfter(moment(end))) {
            return {
                travelDateOrder: 'Start Date must be before End Date'
            };
        }
        return null;
    };

    /** Prior mileage popup: Domestic zip/state; OCONUS area+location on From and To. */
    private validateMileageLocations: ValidatorFn = (
        group: AbstractControl
    ): ValidationErrors | null => {
        const localeId =
            group.get('Locale')?.value?.LocaleID ?? LocaleEnum.Domestic;
        const zip1 = group.get('Zipcode1')?.value;
        const zip2 = group.get('Zipcode2')?.value;
        const state1 = group.get('State1')?.value;
        const state2 = group.get('State2')?.value;
        const city1 = group.get('City1')?.value;
        const city2 = group.get('City2')?.value;

        if (localeId === LocaleEnum.OCONUS) {
            const fromValid =
                !!this.locationText(state1) && !!this.locationText(city1);
            const toValid =
                !!this.locationText(state2) && !!this.locationText(city2);
            if (!fromValid) {
                return {
                    location1: 'From location is required (Area and Location)'
                };
            }
            if (!toValid) {
                return {
                    location2: 'To location is required (Area and Location)'
                };
            }
            return null;
        }

        const hasFrom = this.hasDomesticLocation(zip1, state1);
        const hasTo = this.hasDomesticLocation(zip2, state2);

        if (!hasFrom) {
            return {
                location1: 'From location is required (Zip or State)'
            };
        }
        if (!hasTo) {
            return {
                location2: 'To location is required (Zip or State)'
            };
        }
        return null;
    };

    private hasDomesticLocation(zip: unknown, state: unknown): boolean {
        const zipStr = zip != null ? String(zip).trim() : '';
        const zipValid = zipStr.length > 4;
        const stateValid = !!this.locationText(state);
        return zipValid || stateValid;
    }

    private locationText(value: unknown): string {
        if (value == null) {
            return '';
        }
        if (typeof value === 'object') {
            const obj = value as { State?: string; City?: string };
            return String(obj.State ?? obj.City ?? '').trim();
        }
        return String(value).trim();
    }

    private emitTravelRatesIfNeeded(): void {
        const v = this.entryForm.getRawValue();
        if (this.isPerDiem) {
            if (
                !v.TravelStartDate ||
                !v.TravelEndDate ||
                !v.Locale ||
                !v.PerDiemTypeID ||
                !v.TripLegType
            ) {
                return;
            }
            const localeId = v.Locale.LocaleID;
            const zip = v.Zipcode1 || '';
            // Mobile v1.2: zip XOR city/state for domestic; country/destination for foreign/oconus
            const request: ITravelRate = {
                TravelStart: v.TravelStartDate,
                TravelEnd: v.TravelEndDate,
                LocaleID: localeId,
                PerDiemTypeID: v.PerDiemTypeID,
                TripLegTypeID: v.TripLegType.TripLegTypeID,
                Mileage: 0,
                MileageTypeID: 0,
                ZipCode: '',
                State: '',
                City: '',
                Country_Area: '',
                Destination: ''
            };
            if (localeId === LocaleEnum.Domestic) {
                if (!zip) {
                    request.City = v.City1 || '';
                    request.State = v.State1 || '';
                } else {
                    request.ZipCode = zip;
                }
            } else {
                request.Country_Area = v.Country_Area || '';
                request.Destination = v.Destination_Location || '';
            }
            this.travelRatesNeeded.emit({
                kind: 'perdiem',
                request
            });
        } else if (this.isMileage) {
            if (
                !v.TravelStartDate ||
                !v.TravelEndDate ||
                !v.TravelMode?.RateTypeID ||
                v.Mileage == null
            ) {
                return;
            }
            const mileageNum = Number(v.Mileage);
            if (!(mileageNum > 0)) {
                return;
            }
            const localeId =
                v.Locale?.LocaleID ?? LocaleEnum.Domestic;
            const zip = (v.Zipcode1 || '').trim();
            const state1 = this.locationText(v.State1);
            const city1 = this.locationText(v.City1);

            // #6028 / #5953: do not call rates API until From location is set
            if (localeId === LocaleEnum.OCONUS) {
                if (!state1 || !city1) {
                    return;
                }
            } else if (!zip && !state1) {
                return;
            }

            const request: ITravelRate = {
                TravelStart: v.TravelStartDate,
                TravelEnd: v.TravelEndDate,
                MileageTypeID: v.TravelMode.RateTypeID,
                Mileage: mileageNum,
                LocaleID: localeId,
                PerDiemTypeID: 0,
                TripLegTypeID: 0,
                Country_Area: '',
                Destination: '',
                ZipCode: '',
                City: '',
                State: ''
            };
            if (localeId === LocaleEnum.OCONUS) {
                request.Country_Area = state1;
                request.Destination = city1;
            } else if (!zip) {
                request.City = city1;
                request.State = state1;
            } else {
                request.ZipCode = zip;
            }
            this.travelRatesNeeded.emit({
                kind: 'mileage',
                request
            });
        }
    }

    private buildLinePayload(): IExpenseReportDetailLine {
        // Disabled controls (e.g. ReportDate on existing reports) are omitted from
        // `.value` — use getRawValue so we do not fall back to "today" and move the
        // report WorkDate outside the user's filter range.
        const v = this.entryForm.getRawValue();
        const task = v.SelectedTask as IExpenseTask;
        const item = v.SelectedExpenseItem as IExpenseItem | IExpenseTask;
        const rate = (v.TravelRate || this.selectedMileageRate) as ITravelRate;
        const expReportNbr =
            this.summary?.ExpReportNbr != null && this.summary.ExpReportNbr > 0
                ? this.summary.ExpReportNbr
                : -1;
        const expenseId =
            this.detail?.ExpenseID != null && this.detail.ExpenseID > 0
                ? this.detail.ExpenseID
                : -1;
        const reportDate =
            v.ReportDate ||
            this.summary?.ReportDate ||
            new Date();

        // Field set matches eFAACT Mobile v1.2 ExpenseLineEntryViewModel.Save()
        const line: IExpenseReportDetailLine = {
            ExpReportNbr: expReportNbr,
            ExpenseID: expenseId,
            ERTypeID:
                this.summary?.ERTypeID ?? ExpenseRequestTypeEnum.Expense,
            WorkDate: moment(reportDate).format('MM/DD/YYYY'),
            Purpose: v.Purpose || '',
            EmpAuthID: task?.EmpAuthID,
            TaskLongName: task?.TaskLongName,
            EIListID: (item as any)?.EIListID,
            TravelTypeID: this.travelTypeId,
            row_version:
                (this.detail as any)?.row_version ||
                (this.detail as any)?.Row_Version
        };
        // Always send SequenceId on update so ef_expensereportline_upd does not null it
        if (this.detail?.SequenceId != null && this.detail.SequenceId > 0) {
            line.SequenceId = this.detail.SequenceId;
        }

        if (this.isPerDiem) {
            line.TravelStartDate = moment(v.TravelStartDate).format(
                'MM/DD/YYYY'
            );
            line.TravelEndDate = moment(v.TravelEndDate).format('MM/DD/YYYY');
            line.LocaleID = v.Locale?.LocaleID;
            line.PerDiemTypeID = v.PerDiemTypeID;
            line.TripLegTypeID = v.TripLegType?.TripLegTypeID;
            if (line.LocaleID === LocaleEnum.Domestic) {
                line.Zipcode1 = v.Zipcode1 || '';
                line.State1 = v.State1 || '';
                line.City1 = v.City1 || '';
                line.Country_Area = '';
            } else {
                line.Zipcode1 = '';
                line.State1 = '';
                line.City1 = '';
                line.Country_Area = v.Country_Area || '';
            }
            line.Destination_Location =
                rate?.Destination || v.Destination_Location || '';
            line.Mileage_Rate = rate?.Mileage_Rate;
            line.Rate_Meals = rate?.Meals_Rate;
            line.Rate_Lodging = rate?.Lodging_Rate;
            line.Amount = rate?.Amount;
        } else if (this.isMileage) {
            line.TravelStartDate = moment(v.TravelStartDate).format(
                'MM/DD/YYYY'
            );
            line.TravelEndDate = moment(v.TravelEndDate).format('MM/DD/YYYY');
            line.RateTypeID = v.TravelMode?.RateTypeID;
            line.Mileage = Number(v.Mileage);
            line.Destination_Location = rate?.Destination || '';
            line.Mileage_Rate = rate?.Mileage_Rate;
            line.Rate_Meals = rate?.Meals_Rate;
            line.Rate_Lodging = rate?.Lodging_Rate;
            line.Amount = rate?.Amount;
            line.Zipcode1 = v.Zipcode1 || '';
            line.State1 = v.State1 || '';
            line.City1 = v.City1 || '';
            line.Zipcode2 = v.Zipcode2 || '';
            line.State2 = v.State2 || '';
            line.City2 = v.City2 || '';
            line.LocaleID =
                v.Locale?.LocaleID ?? LocaleEnum.Domestic;
        } else if (this.isReceipt) {
            line.expensereceipt = {
                ReceiptTax: v.ReceiptTax != null ? Number(v.ReceiptTax) : 0,
                ReceiptTotal:
                    v.ReceiptTotal != null ? Number(v.ReceiptTotal) : 0,
                ReceiptDescription: v.ReceiptDescription || '',
                PaymentMethod: v.PaymentMethod?.Paymentmethod,
                creditaccountlistid: v.PaymentMethod?.Creditaccountlistid,
                attachment: this.attachments
            };
        }

        if (
            !this.isReceipt &&
            (v.ReceiptTotal != null ||
                v.ReceiptTax != null ||
                v.ReceiptDescription ||
                v.PaymentMethod ||
                this.attachments.length)
        ) {
            line.expensereceipt = {
                ReceiptTax: v.ReceiptTax != null ? Number(v.ReceiptTax) : 0,
                ReceiptTotal:
                    v.ReceiptTotal != null ? Number(v.ReceiptTotal) : 0,
                ReceiptDescription: v.ReceiptDescription || '',
                PaymentMethod: v.PaymentMethod?.Paymentmethod,
                creditaccountlistid: v.PaymentMethod?.Creditaccountlistid,
                attachment: this.attachments
            };
        }

        return line;
    }

    private ensureDefaultMileageLocale(): void {
        if (!this.isMileage || this.entryForm.value.Locale?.LocaleID) {
            return;
        }
        const domestic = this.mileageLocales.find(
            x => x?.LocaleID === LocaleEnum.Domestic
        );
        if (!domestic) {
            return;
        }
        this.programmaticLocalePatch = true;
        this.entryForm.patchValue({ Locale: domestic }, { emitEvent: false });
        this.programmaticLocalePatch = false;
    }

    private clearMileageLocations(): void {
        this.entryForm.patchValue(
            {
                Zipcode1: null,
                Zipcode2: null,
                State1: null,
                State2: null,
                City1: null,
                City2: null,
                TravelRate: null
            },
            { emitEvent: false }
        );
        this.entryForm.updateValueAndValidity({ emitEvent: false });
    }
}
